import { useState, useMemo } from "react";
import { Box, Stack } from "@mui/material";
import { SightingFilters, SightingsTable, SightingStatsCards } from "../components";
import { sightingsData, getSightingStats } from "../data/sightingsData";
import type { SightingFilters as SightingFiltersType, Sighting } from "../types";

type Order = "asc" | "desc";

const defaultFilters: SightingFiltersType = {
  search: "",
  types: [],
  statuses: [],
  sources: [],
  zones: [],
  dateRange: { start: null, end: null },
};

export const SightingsList = () => {
  const [filters, setFilters] = useState<SightingFiltersType>(defaultFilters);
  const [orderBy, setOrderBy] = useState<string>("dateReported");
  const [order, setOrder] = useState<Order>("desc");

  const stats = useMemo(() => getSightingStats(), []);

  const filteredSightings = useMemo(() => {
    let result = [...sightingsData];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(
        (sighting) =>
          sighting.id.toLowerCase().includes(searchLower) ||
          sighting.referenceNumber.toLowerCase().includes(searchLower) ||
          sighting.description.toLowerCase().includes(searchLower) ||
          sighting.location.toLowerCase().includes(searchLower) ||
          sighting.zone.toLowerCase().includes(searchLower)
      );
    }

    // Apply type filter
    if (filters.types.length > 0) {
      result = result.filter((sighting) => filters.types.includes(sighting.type));
    }

    // Apply status filter
    if (filters.statuses.length > 0) {
      result = result.filter((sighting) => filters.statuses.includes(sighting.status));
    }

    // Apply source filter
    if (filters.sources.length > 0) {
      result = result.filter((sighting) => filters.sources.includes(sighting.source));
    }

    return result;
  }, [filters]);

  const sortedSightings = useMemo(() => {
    const comparator = (a: Sighting, b: Sighting): number => {
      let aValue: string | number;
      let bValue: string | number;

      switch (orderBy) {
        case "id":
          aValue = a.id;
          bValue = b.id;
          break;
        case "referenceNumber":
          aValue = a.referenceNumber;
          bValue = b.referenceNumber;
          break;
        case "type":
          aValue = a.type;
          bValue = b.type;
          break;
        case "status":
          const statusOrder = { open: 0, dispatched: 1, closed: 2 };
          aValue = statusOrder[a.status];
          bValue = statusOrder[b.status];
          break;
        case "source":
          aValue = a.source;
          bValue = b.source;
          break;
        case "location":
          aValue = a.location.toLowerCase();
          bValue = b.location.toLowerCase();
          break;
        case "zone":
          aValue = a.zone;
          bValue = b.zone;
          break;
        case "dateReported":
        default:
          aValue = new Date(a.dateReported).getTime();
          bValue = new Date(b.dateReported).getTime();
          break;
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    };

    return [...filteredSightings].sort(comparator);
  }, [filteredSightings, orderBy, order]);

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Stack spacing={2}>
        <SightingStatsCards stats={stats} />

        <SightingFilters
          filters={filters}
          onFiltersChange={setFilters}
          totalCount={sightingsData.length}
          filteredCount={sortedSightings.length}
        />

        <SightingsTable
          sightings={sortedSightings}
          orderBy={orderBy}
          order={order}
          onSort={handleSort}
        />
      </Stack>
    </Box>
  );
};
